import { useZones } from 'features/Responder/features/Zones';
import { findZoneById, getZoneLockState } from 'features/Responder/features/Zones';
import React from 'react';
import { Button, Card, Col, Form, OverlayTrigger, Row, Stack, Tooltip } from 'react-bootstrap';
import { FaCarSide, FaWalking } from 'react-icons/fa';
import stopLabelFromIndex from 'features/Responder/features/Tours/features/TourBuilder/utils/stopLabelFormIndex';
import StopCard from './StopCard';

const StopList = ({
  stops = [],
  googleApiKey,
  dragIndex,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  removeStop,
  setStops,
  updateStop,
  setAutoBuildPending,
  setSelectedCheckpointId,
  isIndoor,
  stopMetaById = {},
  stopEtaSeconds = [],
  onAddStop,
  canAddStop = true,
  addStopDisabledReason = '',
  startLocation = null,
  showLoopReturn = false,
  destinationMode = 'DRIVING',
  onDestinationModeChange = () => {},
}) => {
  let zonesWithMeta = [];
  try {
    const zonesCtx = useZones();
    zonesWithMeta = zonesCtx?.zonesWithMeta || zonesCtx?.zones || [];
  } catch (err) {
    zonesWithMeta = [];
  }

  const addButton = (
    <Button
      variant="primary"
      onClick={onAddStop}
      disabled={!canAddStop}
      style={!canAddStop ? { pointerEvents: 'none' } : undefined}
    >
      Add Checkpoint
    </Button>
  );

  const originLat = Number(startLocation?.lat);
  const originLng = Number(startLocation?.lng);
  const hasOriginLatLng = Number.isFinite(originLat) && Number.isFinite(originLng);
  const hasOriginLabel =
    typeof startLocation?.address === 'string' && startLocation.address.trim().length > 0;
  const originLabel = hasOriginLabel ? startLocation.address.trim() : 'Start location';
  const currentDestinationMode = (() => {
    const normalized = (destinationMode || '').toString().toUpperCase();
    return normalized === 'WALK' || normalized === 'WALKING' ? 'WALKING' : 'DRIVING';
  })();
  // Show a read-only "return to start" card when looping is enabled and start is set.
  const showLoopCard = showLoopReturn && (hasOriginLatLng || hasOriginLabel);

  return (
    <Stack gap={3}>
      {stops.map((stop, idx) => {
        const aliasLabel =
          typeof stop?.label === 'string' && stop.label.trim().length ? stop.label.trim() : '';
        const computedDisplayName =
          aliasLabel || stopMetaById[stop.id]?.displayName || stopLabelFromIndex(idx);
        const geometryType = (stop?.geometry?.type || 'circle').toLowerCase();
        const zoneId = stop?.zoneId ?? stop?.geometry?.zoneId ?? null;
        const isZoneStop = stop?.geometry?.isZone || geometryType === 'zone' || zoneId != null;
        const resolvedShapeMode = isZoneStop ? 'polygon' : geometryType;
        const zone = findZoneById(zonesWithMeta, zoneId);
        const { locked: readOnlyZone } = getZoneLockState({
          zone,
          forceLocked: isZoneStop,
        });
        return (
          <React.Fragment key={stop.id}>
            <StopCard
              stop={stop}
              idx={idx}
              totalStops={stops.length}
              googleApiKey={googleApiKey}
              isDragging={dragIndex === idx}
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              onRemove={() => removeStop(stop.id)}
              onManualChange={(val) => updateStop(stop.id, 'address', val)}
              onPlaceSelect={(payload) => {
                setStops((prev) =>
                  prev.map((s) =>
                    s.id === stop.id
                      ? {
                          ...s,
                          address: payload.address || s.address,
                          lat: payload.lat ?? s.lat,
                          lng: payload.lng ?? s.lng,
                          placeId: payload.placeId || s.placeId,
                        }
                      : s,
                  ),
                );
                setAutoBuildPending(true);
              }}
              onChangeField={(field, value) => updateStop(stop.id, field, value)}
              etaSeconds={stopEtaSeconds[idx]}
              displayName={computedDisplayName}
              legEtaSeconds={stopMetaById[stop.id]?.legSeconds}
              deadlineSeconds={stopMetaById[stop.id]?.deadlineSeconds}
              totalSeconds={stopMetaById[stop.id]?.totalSeconds}
              tripTotalSeconds={stopMetaById[stop.id]?.tripTotalSeconds}
              legLabel={stopMetaById[stop.id]?.legLabel}
              onFocus={() => {
                setSelectedCheckpointId(`stop-${stop.id || idx}`);
              }}
              isIndoor={isIndoor}
              mode={stop.mode || 'DRIVING'}
              onModeChange={(nextMode) => {
                updateStop(stop.id, 'mode', nextMode);
                setAutoBuildPending(true);
              }}
              shapeMode={resolvedShapeMode}
              readOnlyZone={readOnlyZone}
              onShapeModeChange={(nextShapeMode) => {
                // Map shape mode to geometry type
                const geometryTypeMap = {
                  circle: 'circle',
                  polygon: 'polygon',
                  crossline: 'crossline',
                };

                // Create or update geometry
                const requestedType = geometryTypeMap[nextShapeMode] || 'circle';
                const currentType = (stop.geometry?.type || '').toLowerCase();
                const nextType =
                  currentType === 'zone' && requestedType === 'polygon' ? 'zone' : requestedType;
                const newGeometry = {
                  ...stop.geometry,
                  type: nextType,
                };

                updateStop(stop.id, 'geometry', newGeometry);
                setAutoBuildPending(false);
              }}
            />
          </React.Fragment>
        );
      })}

      {showLoopCard && (
        <Card className="shadow-sm bg-primary-shadow text-theme border-theme-strong">
          <Card.Body className="p-3">
            <div className="fw-semibold">Return to Start</div>
            <div className="text-theme-muted small mb-2">Looped destination</div>
            <div className="mt-2">
              <div className="fw-semibold text-uppercase text-theme-muted small mb-2">
                Travel mode
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant={currentDestinationMode === 'DRIVING' ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => onDestinationModeChange('DRIVING')}
                >
                  <div className="d-flex align-items-center gap-2">
                    <FaCarSide />
                    <span>Driving</span>
                  </div>
                </Button>
                <Button
                  variant={currentDestinationMode === 'WALKING' ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => onDestinationModeChange('WALKING')}
                >
                  <div className="d-flex align-items-center gap-2">
                    <FaWalking />
                    <span>Walking</span>
                  </div>
                </Button>
              </div>
            </div>
            <hr className="my-3 opacity-50" />

            <Form.Group className="mt-2">
              <Form.Label className="small text-theme-muted mb-1">Address</Form.Label>
              <Form.Control
                className="border-0 responder-input"
                size="sm"
                value={originLabel}
                readOnly
              />
              {hasOriginLatLng && (
                <Row className="mt-2 g-2">
                  <Col>
                    <Form.Label className="small text-theme-muted mb-1">Latitude</Form.Label>
                    <Form.Control
                      className="border-0 responder-input"
                      size="sm"
                      value={originLat}
                      readOnly
                    />
                  </Col>
                  <Col>
                    <Form.Label className="small text-theme-muted mb-1">Longitude</Form.Label>
                    <Form.Control
                      className="border-0 responder-input"
                      size="sm"
                      value={originLng}
                      readOnly
                    />
                  </Col>
                </Row>
              )}
            </Form.Group>
          </Card.Body>
        </Card>
      )}

      <div className="d-flex flex-column align-items-center gap-1">
        {canAddStop || !addStopDisabledReason ? (
          addButton
        ) : (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip className="responder-tooltip">{addStopDisabledReason}</Tooltip>}
          >
            <span className="d-inline-block" style={{ cursor: 'not-allowed' }}>
              {addButton}
            </span>
          </OverlayTrigger>
        )}
      </div>
    </Stack>
  );
};

export default StopList;


